import React from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  AddToCartButton,
  CardContent,
  CardInfo,
  CardPartnerLogo,
  InBasketButton,
  LogoItem,
  MoreInfoButton,
  Partner,
  PriceText,
  PricesContainer,
  StatusBar,
  StatusBarText,
  StyledButtonContainer,
  StyledLink,
} from "./styles";

import FeatureWrapper from "components/FeatureToggle/FeatureWrapper";
import { addOfferToCart, checkIfOfferIsInCart } from "state/cart-slice";
import { monetaryConfig, sendULFEvent } from "state/config-slice";
import { AppDispatch } from "state/reducers-combiner";
import Offer, { Status } from "types/Offer";
import { ULFEvents } from "types/ULF";
import generateCurrencyValue from "utils/generateCurrencyValue";
import getFeature, { isFeatureActive } from "utils/getFeature";
import useImageValidation from "hooks/useImageValidation";

interface IOfferCardProps {
  offer: Offer;
  numberOfPendingActivationOffers?: number;
  iscrossselling?: boolean;
}

const OfferCard = ({ offer, numberOfPendingActivationOffers, iscrossselling = false }: IOfferCardProps) => {
  const {
    name,
    partner,
    marketplaceInfo,
    upgradable,
    downgradable,
    trial,
    endDate,
    type,
    multiPlanOffer,
    fromPurchasedArray,
  } = offer || {};

  const offerIsInCart = useSelector(checkIfOfferIsInCart(name));

  const dispatch: AppDispatch = useDispatch();
  const monetarySettings = useSelector(monetaryConfig);
  const { t } = useTranslation();
  const [cookies] = useCookies();
  const navigate = useNavigate();
  const hasTrialStatusBar =
    (marketplaceInfo?.status === Status.purchasable || marketplaceInfo?.status === Status.purchasableExternally) &&
    trial;

  const imageSource = iscrossselling ? offer?.images?.logoLandscapeWhite : offer?.images?.logoWhite;

  const { imageExists } = useImageValidation(imageSource);

  const isStartingPrice = marketplaceInfo?.isStartingPrice ?? marketplaceInfo?.agency;

  const isWeekly = offer?.duration?.name === "WEEKLY";

  return (
    <StyledLink
      to={`${multiPlanOffer && !fromPurchasedArray ? `/multiplanoffer/${name}` : `/offer/${name}`}`}
      id={`offer_${partner}`}
      data-testid={`offer_${offer.name}`}
      $iscrossselling={iscrossselling}
    >
      {marketplaceInfo?.status === Status.provisioning && (
        <StatusBar color={"black"} $status={Status.provisioning} data-testid={"statusbar-provisioning"}>
          <StatusBarText>{t("In progress")}</StatusBarText>
        </StatusBar>
      )}
      {hasTrialStatusBar && (
        <StatusBar color={"blue"} data-testid="statusbar-trial">
          <StatusBarText>{t("{{ tag }}", { tag: marketplaceInfo.tag })}</StatusBarText>
        </StatusBar>
      )}
      {(marketplaceInfo?.status === Status.active ||
        (marketplaceInfo?.status === Status.cancelled && Date.now() <= Date.parse(endDate as string))) && (
        <StatusBar color={"black"} data-testid="statusbar-active">
          <StatusBarText>{t("Active")}</StatusBarText>
        </StatusBar>
      )}
      {marketplaceInfo?.status === Status.pendingActivation &&
        numberOfPendingActivationOffers &&
        numberOfPendingActivationOffers > 1 && (
          <StatusBar color={"yellow"} data-testid="statusbar-setup-now-yellow">
            <StatusBarText>
              {type === "hardbundle" ? t("Select inclusive offer") : t("Ready to Set up now")}
            </StatusBarText>
          </StatusBar>
        )}
      {marketplaceInfo?.status === Status.pendingActivation &&
        numberOfPendingActivationOffers &&
        numberOfPendingActivationOffers === 1 && (
          <StatusBar color={"red"} data-testid="statusbar-setup-now-red">
            <StatusBarText>{t("Ready to Set up now")}</StatusBarText>
          </StatusBar>
        )}
      {marketplaceInfo?.status === Status.cardPending && (
        <StatusBar color={"black"} data-testid="statusbar-card-pending">
          <StatusBarText>{t("Card pending")}</StatusBarText>
        </StatusBar>
      )}
      {marketplaceInfo?.status === Status.cardExpired && (
        <StatusBar color={"white"} data-testid="statusbar-card-expired">
          <StatusBarText>{t("Card expired")}</StatusBarText>
        </StatusBar>
      )}
      <CardContent
        $hasStatusBar={hasTrialStatusBar}
        $backgroundImages={iscrossselling ? offer.images?.cross : offer.images?.card}
        $iscrossselling={iscrossselling}
        $status={marketplaceInfo?.status}
        $darkenBackgroundImage={true}
        data-testid={`card-content`}
      >
        <FeatureWrapper name="image guidelines V1">
          {getFeature("image guidelines V1")?.options.partnersToOverlayTheLogo.includes(offer.partner) && (
            <CardPartnerLogo
              active={marketplaceInfo?.status === Status.active}
              $iscrossselling={iscrossselling}
              $status={marketplaceInfo?.status}
            >
              <LogoItem $iscrossselling={iscrossselling}>
                <picture>
                  {iscrossselling ? (
                    <img src={offer.images?.logoLandscape} alt={`${offer.partner} logo`} />
                  ) : (
                    <img src={offer.images?.logo} alt={`partner-${partner}-logo`} />
                  )}
                </picture>
              </LogoItem>
            </CardPartnerLogo>
          )}
        </FeatureWrapper>
        {imageExists && (
          <FeatureWrapper name="image guidelines V2">
            <CardPartnerLogo
              active={marketplaceInfo?.status === Status.active}
              $iscrossselling={iscrossselling}
              $status={marketplaceInfo?.status}
            >
              <LogoItem $iscrossselling={iscrossselling}>
                <picture data-testid={"card-icon"}>
                  {iscrossselling ? (
                    <img src={offer.images?.logoLandscapeWhite} alt={`${offer.partner} logo`} />
                  ) : (
                    <img src={offer.images?.logoWhite} alt={`partner-${partner}-logo`} />
                  )}
                </picture>
              </LogoItem>
            </CardPartnerLogo>
          </FeatureWrapper>
        )}
        <CardInfo
          $iscrossselling={iscrossselling}
          $isMoreInfoButton={
            marketplaceInfo?.status !== Status.purchasable && marketplaceInfo?.status !== Status.purchasableExternally
          }
        >
          {(marketplaceInfo?.status === Status.purchasable ||
            marketplaceInfo?.status === Status.purchasableExternally ||
            ((upgradable || downgradable) && type !== "hardbundle")) && (
            <PricesContainer data-testid="prices-container" $iscrossselling={iscrossselling}>
              <Partner $iscrossselling={iscrossselling} data-testid={"prices-container-partner"}>
                {multiPlanOffer ? marketplaceInfo?.logoName : marketplaceInfo?.title}
              </Partner>
              <PriceText data-testid={"prices-text"}>
                {isStartingPrice
                  ? t(isWeekly ? "From {{ price }}/wk." : "From {{ price }}/mo.", {
                      price: generateCurrencyValue(marketplaceInfo?.price, monetarySettings),
                    })
                  : t(
                      isWeekly
                        ? "{{ price }}/wk."
                        : multiPlanOffer
                        ? "Starting From {{ price }}/mo."
                        : "{{ price }}/mo.",
                      {
                        price: generateCurrencyValue(marketplaceInfo?.price, monetarySettings),
                      },
                    )}
              </PriceText>
            </PricesContainer>
          )}
          {marketplaceInfo?.status === Status.purchasable && (
            <>
              {offerIsInCart ? (
                <StyledButtonContainer
                  $iscrossselling={iscrossselling}
                  $isMoreInfoButton={false}
                  $isInBasketButton={true}
                  data-testid="in-basket-button"
                >
                  <InBasketButton
                    id={`in_basket_${offer.name}`}
                    title={t("In Basket")}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();

                      navigate("/basket");
                      return;
                    }}
                  />
                </StyledButtonContainer>
              ) : (
                <StyledButtonContainer
                  $iscrossselling={iscrossselling}
                  $isMoreInfoButton={false}
                  $isInBasketButton={false}
                  data-testid="add-to-basket-button"
                >
                  <AddToCartButton
                    id={`add_${offer.name}`}
                    title={multiPlanOffer ? t("View Plans") : type === "hardbundle" ? t("Select") : t("Add")}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();

                      if (
                        !isFeatureActive("T&Cs checkbox") &&
                        !isFeatureActive("confirm activation checkbox") &&
                        !isFeatureActive("Tile add btn only redirects")
                      ) {
                        dispatch(addOfferToCart({ ...offer }));
                      }

                      if (multiPlanOffer && !fromPurchasedArray) {
                        navigate(`/multiplanoffer/${name}`);
                      } else {
                        navigate(`/offer/${offer.name}`);
                      }

                      dispatch(
                        sendULFEvent({
                          page: window.location.pathname,
                          "event-name": ULFEvents.BASKET_ADD,
                          "offer-name": name,
                          partner,
                          source: cookies.SOURCE,
                          isTrackingAllowed: cookies.SUBSCRIPTIONPLUS_TRACKING || false,
                        }),
                      );
                    }}
                  />
                </StyledButtonContainer>
              )}
            </>
          )}
          {marketplaceInfo?.status !== Status.purchasable && (
            <StyledButtonContainer
              data-testid="cta-container"
              $iscrossselling={false}
              $isMoreInfoButton={true}
              $isInBasketButton={false}
            >
              <MoreInfoButton
                id={`cta_${offer.name}`}
                title={
                  marketplaceInfo?.status === Status.pendingActivation ||
                  marketplaceInfo?.status === Status.purchasableExternally
                    ? multiPlanOffer
                      ? t("View Plans")
                      : type === "hardbundle"
                      ? t("Select")
                      : t("Set up")
                    : t("More Info")
                }
                onClick={(event: { preventDefault: () => void; stopPropagation: () => void }) => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigate(`${multiPlanOffer && !fromPurchasedArray ? `/multiplanoffer/${name}` : `/offer/${name}`}`);
                }}
              ></MoreInfoButton>
            </StyledButtonContainer>
          )}
        </CardInfo>
      </CardContent>
    </StyledLink>
  );
};

export default OfferCard;
